import { offlineDB } from './indexedDB';
import api from '../api';

export interface OfflineState {
  isOnline: boolean;
  isSyncing: boolean;
  pendingCount: number;
  lastSyncTime: number | null;
  lastError: string | null;
}

type OfflineStateListener = (state: OfflineState) => void;

class OfflineManager {
  private state: OfflineState = {
    isOnline: true,
    isSyncing: false,
    pendingCount: 0,
    lastSyncTime: null,
    lastError: null
  };
  private listeners: Set<OfflineStateListener> = new Set();
  private syncInProgress = false;
  private syncInterval: number | null = null;
  private initialized = false;

  constructor() {
    if (typeof window !== 'undefined') {
      this.init();
    }
  }

  init() {
    if (this.initialized) return;
    this.initialized = true;
    window.addEventListener('online', () => this.handleOnline());
    window.addEventListener('offline', () => this.handleOffline());
    
    this.updateState({ isOnline: navigator.onLine });
    this.updatePendingCount();
    
    this.syncInterval = window.setInterval(() => this.syncPendingItems(), 30000);
    
    if ('sync' in ServiceWorkerRegistration.prototype) {
      this.registerBackgroundSync();
    }
  }

  private handleOnline() {
    this.updateState({ isOnline: true });
    this.syncPendingItems();
  }

  private handleOffline() {
    this.updateState({ isOnline: false });
  }

  private updateState(updates: Partial<OfflineState>) {
    this.state = { ...this.state, ...updates };
    this.notifyListeners();
  }

  private notifyListeners() {
    this.listeners.forEach(listener => listener(this.state));
  }

  subscribe(listener: OfflineStateListener): () => void {
    this.listeners.add(listener);
    listener(this.state);
    return () => this.listeners.delete(listener);
  }

  getState(): OfflineState {
    return { ...this.state };
  }

  private async updatePendingCount() {
    const queue = await offlineDB.getSyncQueue();
    const pending = await offlineDB.getPendingItems();
    this.updateState({ pendingCount: queue.length + pending.length });
  }

  async queueMutation(action: 'create' | 'update' | 'delete', endpoint: string, data: any): Promise<void> {
    await offlineDB.addToSyncQueue(action, endpoint, data);
    this.updatePendingCount();
    
    if ('sync' in ServiceWorkerRegistration.prototype && navigator.onLine) {
      try {
        const registration = await navigator.serviceWorker.ready;
        await (registration as any).sync.register('sync-mutations');
      } catch (e) {
        console.log('Background sync registration skipped:', e);
      }
    }
  }

  async syncPendingItems(): Promise<void> {
    if (!navigator.onLine || this.syncInProgress) return;
    
    this.syncInProgress = true;
    this.updateState({ isSyncing: true, lastError: null });

    try {
      const queue = await offlineDB.getSyncQueue();
      
      for (const item of queue) {
        try {
          const method = item.action === 'create' ? 'POST' : item.action === 'update' ? 'PUT' : 'DELETE';
          
          await api({
            method,
            url: item.endpoint,
            data: item.data
          });
          
          await offlineDB.removeFromSyncQueue(item.id);
        } catch (error: any) {
          if (error.response?.status === 401 || error.response?.status === 403) {
            await offlineDB.updateSyncQueueItem(item.id, { retries: item.retries + 1 });
          } else if (item.retries >= 3) {
            await offlineDB.removeFromSyncQueue(item.id);
          } else {
            await offlineDB.updateSyncQueueItem(item.id, { retries: item.retries + 1 });
          }
        }
      }

      this.updateState({ lastSyncTime: Date.now() });
    } catch (error: any) {
      this.updateState({ lastError: error.message || 'Sync failed' });
    } finally {
      this.syncInProgress = false;
      this.updateState({ isSyncing: false });
      this.updatePendingCount();
    }
  }

  private async registerBackgroundSync() {
    try {
      const registration = await navigator.serviceWorker.ready;
      await (registration as any).sync.register('sync-mutations');
    } catch (e) {
      console.log('Background sync registration skipped');
    }
  }

  async clearOfflineData(): Promise<void> {
    await offlineDB.clearAll();
    this.updateState({
      pendingCount: 0,
      lastSyncTime: null,
      lastError: null
    });
  }

  destroy() {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
    }
  }
}

export const offlineManager = new OfflineManager();
export default offlineManager;